import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api"; // Ensure this path is correct based on your project structure
import {
  GlobeAltIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { isUrl } from "../utils/isUrl";
import { Tooltip } from "flowbite-react";
import OnboardingWrapper from "../components/OnboardingWrapper";
import OnboardingProgressBar from "./OnboardingProgressBar";
import PrimaryButton from "../components/PrimaryButton";
import Input from "../components/Input";
import PrimaryLinkButton from "../components/PrimaryLinkButton";
import { useDebouncedCallback } from "use-debounce";
import { getOnboardingData, setOnboardingData } from "../utils/onboarding";
import { useCompanyApi } from "../hooks/useCompanyApi";
import useUploadingProgress from "../hooks/useUploadingProgress";

const EnterWebsiteURL = () => {
  const onboardingData = getOnboardingData();
  const { updateCompany, company, fetchCompany } = useCompanyApi();
  const [urls, setUrls] = useState(onboardingData.urls || ["", ""]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { progress, message, setProgress, setCount } = useUploadingProgress({ company });

  const change = (e, idx) => {
    const _urls = [...urls];
    _urls[idx] = e.target.value;
    setUrls(_urls);
  };

  const onSubmit = async e => {
    try {
      e.preventDefault();

      setProgress(0);
      setLoading(true);

      const validUrls = urls.filter(isUrl);
      const total = validUrls.length;
      if (total === 0) {
        alert("Please enter at least one valid URL.");
        return;
      }
      setCount(total);
      let successCount = 0;

      for (let i = 0; i < total; i++) {
        const url = validUrls[i];

        try {
          const response = await api.post("/api/process-url", { url });
          if (response.status === 200) {
            successCount++;
          }
        } catch (error) {
          console.warn(`Failed to process URL: ${url}`, error);
        }
      }

      if (successCount === 0) {
        return alert("Failed to process any URL");
      }

      await updateCompany({ registration_step: "upload-content" });

      setTimeout(() => navigate("/onboarding/upload-content"), 500);
    } catch (error) {
      alert(error.message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  const disabled = !Boolean(urls.some(i => isUrl(i)));

  const addRow = () => setUrls(prev => [...prev, ""]);

  const remove = idx => {
    setUrls(prev => prev.filter((_, _idx) => _idx !== idx));
  };

  const saveUrlsDebounced = useDebouncedCallback(
    async urls => {
      setOnboardingData({ urls });
    },
    // delay in ms
    1000
  );

  useEffect(() => {
    saveUrlsDebounced(urls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urls]);

  useEffect(() => {
    fetchCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OnboardingWrapper
      currentStep="website-url"
      onSubmit={onSubmit}
      actions={
        <>
          <div className="pb-20 justify-center w-full hidden sm:flex">
            <OnboardingProgressBar currentStep={1} />
          </div>

          <div className="sm:hidden block w-full">
            <PrimaryButton disabled={disabled} loading={loading}>
              Continue
            </PrimaryButton>
          </div>
        </>
      }
    >
      <div className="sm:max-w-md w-full flex flex-col gap-6 mt-8 sm:mt-0 px-4 sm:px-0 mb-6 sm:mb-0">
        <div>
          <h1 className="text-3xl font-semibold leading-9 text-gray-900 text-center">
            Enter your website
          </h1>
          <p className="text-base font-normal leading-normal text-center text-slate-600">
            We’ll scan each URL you enter to train your AI Agent
          </p>
        </div>

        {urls.map((url, idx) => (
          <div key={idx}>
            <div className="flex items-center justify-between gap-4">
              {idx === 0 && (
                <label
                  htmlFor="url"
                  className="block text-sm font-medium leading-tight text-slate-700"
                >
                  Homepage <span className="text-primary">*</span>
                </label>
              )}

              {idx === 1 && (
                <label
                  htmlFor="url"
                  className="text-sm font-medium leading-tight text-slate-700 flex gap-1"
                >
                  <span>Other URL(s) - (optional)</span>{" "}
                  <Tooltip
                    theme={{
                      target: "flex",
                    }}
                    content={
                      <div className="max-w-sm">
                        <p className="p-0 m-0 font-semibold">Other URL(s)</p>
                        <span className="text-xs">
                          Add support-focused pages like your pricing, FAQ, Help
                          Center, return policy, or other policy pages. These
                          help your AI agent respond accurately based on your
                          most important and up-to-date content.
                        </span>
                      </div>
                    }
                  >
                    <QuestionMarkCircleIcon className="size-5 cursor-pointer" />
                  </Tooltip>
                </label>
              )}
            </div>
            <div className="mt-2 grid grid-cols-1">
              <Input
                onReset={idx > 0 ? () => remove(idx) : undefined}
                startIcon={<GlobeAltIcon className="size-6 text-gray-400" />}
                value={urls[idx]}
                onChange={e => change(e, idx)}
                required={idx === 0}
                placeholder="example.com"
              />
            </div>
          </div>
        ))}

        <PrimaryLinkButton onClick={addRow} type="button">
          + Add another
        </PrimaryLinkButton>

        {loading && (
          <div>
            <div className="flex items-center gap-4">
              <div className="overflow-hidden rounded-full bg-gray-200 flex-1">
                <div
                  style={{ width: `${progress}%` }}
                  className="h-2 rounded-full bg-primary transition-all"
                />
              </div>
              <span className="text-xs font-semibold">{progress}%</span>
            </div>
            {message && (
              <p className="text-base font-normal leading-normal text-center text-slate-600">
                {message}
              </p>
            )}
          </div>
        )}

        <div className="hidden sm:block">
          <PrimaryButton disabled={disabled} loading={loading}>
            Continue
          </PrimaryButton>
        </div>
      </div>
    </OnboardingWrapper>
  );
};

export default EnterWebsiteURL;
